<template>
	<div>
		<div class="flex flex-col lg:flex-row">
			<div class="w-full lg:w-1/2">
				<BaseInput ref="BaseInput1" :label="$t('recipient')" class="w-1/2" v-model="mailRecipient" :required="true" validationtype="email" />
				<BaseInput ref="BaseInput2" :label="$t('subject')" v-model="mailSubject" :required="true" />
				<BaseInput ref="BaseInput3" :label="$t('text')" v-model="mailText" :required="true">
					<textarea v-model="mailText" class="textarea" />
				</BaseInput>

				<div class="text-left mb-3">
					<div class="font-bold">
						{{ $t('appendix') }} <span class="text-sm text-gray-500">{{ $t('if_set') }}</span>
					</div>
					<div @click="getRepairExtensionFile" class="cursor-pointer">
						<font-awesome-icon :icon="['fas', 'angle-right']" class="mr-1"></font-awesome-icon>
						{{ $t('repair_extension') }}
					</div>
				</div>
			</div>

			<div v-if="imageFiles" class="lg:w-1/2 lg:ml-8 lg:mr-4 w-full">
				<div class="text-left mb-2">
					<b>{{ $t('pick_images') }}</b>
				</div>
				<div class="flex flex-wrap -mx-2">
					<div v-for="metaImage in imageFiles" :key="metaImage.id" class="flex-grow mx-2 mb-4">
						<MetaInfoImage ref="MetaInfoImage" :meta-info-media="metaImage" v-model="metaImage.isSelected" :chooseable="true" :deletable="false" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import BaseInput from '@/components/forms/inputs/BaseInput';
import defaultConfiguration from '@/api/defaultConfiguration';
import { DamagecasesApiFactory } from '@/api/generated';
import MetaInfoImage from '@/components/damagecases/forms/MetaInfoImage';
import apiDefaults from '@/api/defaultConfiguration';
import { MetaInfoFileTypeEnum, RepairExtensionStatusEnum } from '@/api/generated/models';
import { emptyCatch } from '@/plugins/Utils';
import Validate from '@/mixins/Validate';
import RepairExtensionsRepository from '@/api/repositories/RepairExtensionsRepository';
import PDFDamagecasesRepository from '@/api/repositories/PDFDamagecasesRepository';

export default {
	name: 'RepairExtensionMailLawyer',
	components: { MetaInfoImage, BaseInput },
	mixins: [Validate],
	data() {
		return {
			mailText: null,
			mailSubject: null,
			mailRecipient: null,
			imageFiles: [],
			repairExtension: null,
		};
	},
	methods: {
		save() {
			if (this.validate()) {
				this.$swal({
					title: this.$t('sure').toString(),
					text: this.$t('send_mail_appendix_to', { recipient: this.mailRecipient }),
					icon: 'warning',
					cancelButtonText: this.$t('general.no').toString(),
					showCancelButton: true,
					confirmButtonText: this.$t('general.yes').toString(),
				}).then(result => {
					if (result.value) {
						new RepairExtensionsRepository()
							.sentEmailToLawyerOffice(this.$route.params.processId || this.$route.params.id, this.$route.params.extensionID, {
								subject: this.mailSubject,
								text: this.mailText,
								recipient: this.mailRecipient,
								images: this.activeImageFiles.map(file => file.id),
							})
							.then(result => {
								this.$store.commit('setConfirmLeaving', false);
								this.$swal(this.$t('general.success').toString(), this.$t('e_mail_sent').toString(), 'success').then(() => {
									this.$router.push({ name: 'damagecases.edit' });
								});
							});
					}
				});
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
		loadFiles(repairExtension) {
			return DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdRepairextensionsExtidMediafilesGet(parseInt(this.$route.params.processId || this.$route.params.id), repairExtension)
				.then(result => {
					result.data.forEach(file => {
						if (file.type === MetaInfoFileTypeEnum.Image) {
							this.imageFiles.push(file);
						}
					});
				})
				.catch(error => {});
		},
		getRepairExtensionFile() {
			const uuid = this.repairExtension.pdf ?? this.repairExtension.zip;
			const isPDF = this.repairExtension.pdf != null;
			new PDFDamagecasesRepository()
				.getSavedDocument(uuid)
				.then(response => {
					const label = response.headers['content-disposition'].split('filename=')[1];
					const blob = new Blob([response.data], { type: 'application/pdf' });
					const url = window.URL.createObjectURL(blob);

					if (isPDF) {
						const viewerUrl = `/blobviewer?blobUrl=${encodeURIComponent(url)}&title=${encodeURIComponent(label)}`;
						setTimeout(function() {
							window.open(viewerUrl, '_blank');
							window.URL.revokeObjectURL(viewerUrl);
						}, 200);
					} else {
						this.$swal({
							title: this.$t('sure').toString(),
							text: this.$t('no_pdf_for_repair_extension').toString(),
							icon: 'warning',
							cancelButtonText: this.$t('general.no').toString(),
							showCancelButton: true,
							confirmButtonText: this.$t('general.yes').toString(),
						}).then(result => {
							if (result.value) {
								setTimeout(function() {
									const a = document.createElement('a');
									a.style.display = 'none';
									a.href = url;
									a.download = label;
									document.body.appendChild(a);
									a.click();
									window.URL.revokeObjectURL(url);
								}, 200);
							}
						});
					}
				})
				.catch(e => {
					this.$swal(this.$t('general.error').toString(), this.$t('document_could_not_be_downloaded').toString(), 'error');
				});
		},
	},
	computed: {
		activeImageFiles() {
			return this.imageFiles.filter(file => {
				return file.isSelected;
			});
		},
	},
	mounted() {
		this.$store.dispatch('updateDamageCase', { id: this.$route.params.processId || this.$route.params.id }).then(response => {
			this.mailRecipient = this.$store.getters.getDamageCase.lawyerOffice.email;
			this.mailSubject = (
				this.$t('repair_extension') +
				' ' +
				this.$t('imd_connect_number') +
				' ' +
				(this.$store.getters.getDamageCase?.imdNetId || '')
			).trim();
			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdRepairextensionsExtidGet(
					parseInt(this.$route.params.processId || this.$route.params.id),
					parseInt(this.$route.params.extensionID),
				)
				.then(result => {
					this.repairExtension = result.data;
					this.loadFiles(parseInt(this.$route.params.extensionID));
				})
				.catch(emptyCatch);
		});

		DamagecasesApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
			.damagecasesIdTextgeneratorTypePost('l3_mail_repairextension', this.$route.params.processId || this.$route.params.id, {
				repairExtensionID: this.$route.params.extensionID,
			})
			.then(response => {
				this.mailText = response.data;
			})
			.catch(error => {
				this.$swal(this.$t('general.error').toString(), this.$t('mail_text_generation_error').toString(), 'error');
			});
	},
};
</script>

<style scoped>
.textarea {
	height: 36rem;
}
</style>
